import React, { useCallback, useEffect, useState } from 'react';
import { adminWorkflow, workflowError, displayDate } from '../utils/electionWorkflows';
import WorkflowPagination from './WorkflowPagination';

export default function ElectionNdas({ electionId }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({ title: '', content: '' });
  const [recipients, setRecipients] = useState([{ name: '', email: '' }]);
  const [results, setResults] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminWorkflow('get', `${electionId}/ndas?page=${page}`);
      setData(response.data.data);
    } catch (err) { setError(workflowError(err)); }
    finally { setLoading(false); }
  }, [electionId, page]);
  useEffect(() => { load(); }, [load]);
  const agreement = data?.agreement;
  useEffect(() => {
    if (agreement) setDraft({ title: agreement.title, content: agreement.content });
  }, [agreement?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = draft.title !== agreement?.title || draft.content !== agreement?.content;
  const run = async action => {
    setBusy(true);
    setError('');
    setMessage('');
    try { await action(); }
    catch (err) { setError(workflowError(err)); }
    finally { setBusy(false); }
  };
  const save = event => {
    event.preventDefault();
    run(async () => {
      const response = await adminWorkflow('post', `${electionId}/ndas/agreements`, draft);
      setData(previous => ({ invitations: [], total: 0, ...previous, agreement: response.data.data }));
      setMessage(response.data.message);
    });
  };
  const send = event => {
    event.preventDefault();
    run(async () => {
      const response = await adminWorkflow('post', `${electionId}/ndas/send`, { agreementId: agreement.id, recipients });
      setResults(response.data.data);
      setMessage(response.data.message);
      await load();
    });
  };
  const resend = invitation => run(async () => {
    try {
      const response = await adminWorkflow('post', `${electionId}/ndas/${invitation.id}/resend`, {});
      setMessage(response.data.message);
    } finally { await load(); }
  });
  return <details className="card mb-4">
    <summary className="card-header fw-bold p-3">Election NDA &amp; acceptance tracking</summary>
    <div className="card-body">
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      {message && <div className="alert alert-info" role="status">{message}</div>}
      <p>Provide your organisation’s non-disclosure agreement. Each invitation keeps the exact version sent.
        This records acknowledgement, not a third-party verified e-signature, and does not restrict voting or nominations.</p>
      {loading && <p role="status">Loading NDA records...</p>}
      <form onSubmit={save} className="mb-4">
        <label className="form-label" htmlFor="nda-title">NDA title</label>
        <input id="nda-title" className="form-control mb-3" maxLength={255} required value={draft.title}
          onChange={event => setDraft({ ...draft, title: event.target.value })} />
        <label className="form-label" htmlFor="nda-content">Agreement text</label>
        <textarea id="nda-content" className="form-control mb-3" rows={8} maxLength={50000} required value={draft.content}
          onChange={event => setDraft({ ...draft, content: event.target.value })} />
        <button className="btn btn-primary" disabled={busy || loading || !dirty}>Save NDA version</button>
        {agreement && <span className="ms-3">Saved version: {agreement.version}</span>}
        {agreement && dirty && <p className="small text-warning mt-2">Save your changes as a new version before sending.</p>}
      </form>
      <form onSubmit={send} className="mb-4">
        <h3 className="h6">Send the saved NDA to recipients</h3>
        {recipients.map((recipient, index) => <div className="row g-2 mb-2" key={index}>
          <div className="col-md-5">
            <label className="visually-hidden" htmlFor={`nda-name-${index}`}>Recipient {index + 1} name</label>
            <input id={`nda-name-${index}`} className="form-control" placeholder="Full name" maxLength={255} required value={recipient.name}
              onChange={event => setRecipients(recipients.map((item, i) => i === index ? { ...item, name: event.target.value } : item))} />
          </div>
          <div className="col-md-5">
            <label className="visually-hidden" htmlFor={`nda-email-${index}`}>Recipient {index + 1} email</label>
            <input id={`nda-email-${index}`} className="form-control" type="email" placeholder="Email address" maxLength={254} required value={recipient.email}
              onChange={event => setRecipients(recipients.map((item, i) => i === index ? { ...item, email: event.target.value } : item))} />
          </div>
          <div className="col-md-2">
            {recipients.length > 1 && <button type="button" className="btn btn-outline-danger" disabled={busy}
              onClick={() => setRecipients(recipients.filter((_, i) => i !== index))}>Remove</button>}
          </div>
        </div>)}
        <div className="d-flex gap-2 mt-3">
          <button type="button" className="btn btn-outline-secondary" disabled={busy || recipients.length >= 25}
            onClick={() => setRecipients([...recipients, { name: '', email: '' }])}>Add recipient</button>
          <button className="btn btn-success" disabled={busy || loading || !agreement || dirty}>Send NDA invitations</button>
        </div>
        <p className="small text-muted mt-2">Up to 25 recipients per batch. Invitation links expire after 7 days.
          “Pending acceptance” means submitted to the email provider, not confirmed delivery.</p>
      </form>
      {results.length > 0 && <ul aria-label="Invitation send results">
        {results.map(item => <li key={item.id}>{item.email}: {item.status}</li>)}
      </ul>}
      <div className="d-flex justify-content-between mb-3">
        <h3 className="h6">Invitations and acceptances ({data?.total || 0})</h3>
        <button className="btn btn-sm btn-outline-secondary" disabled={busy || loading} onClick={load}>Refresh NDA statuses</button>
      </div>
      <div className="table-responsive">
        <table className="table align-middle">
          <thead><tr><th>Recipient</th><th>Agreement</th><th>Status</th><th>Acceptance</th><th>Actions</th></tr></thead>
          <tbody>{data?.invitations.map(item => <tr key={item.id}>
            <td>{item.recipientName}<small className="d-block">{item.recipientEmail}</small></td>
            <td>{item.agreementTitle || 'NDA'} · v{item.agreementVersion || '—'}</td>
            <td>{item.status}<small className="d-block text-muted">Expires {displayDate(item.expiresAt)}</small></td>
            <td>{item.acceptedAt ? <>{item.signedName}<small className="d-block">{displayDate(item.acceptedAt)}</small></> : 'Not yet accepted'}</td>
            <td>{!item.acceptedAt && <button className="btn btn-sm btn-outline-primary" disabled={busy} onClick={() => resend(item)}>Resend</button>}</td>
          </tr>)}</tbody>
        </table>
      </div>
      {data && !data.invitations.length && <p>No NDA invitations sent yet.</p>}
      <WorkflowPagination page={page} total={data?.total || 0} onChange={setPage} disabled={loading || busy} />
    </div>
  </details>;
}
